<div class="flex flex-col gap-4 p-4">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-default text-xl font-bold">Support Dashboard</h1>
      <p class="text-secondary text-sm">Service request tracking, SLA compliance and categorisation</p>
    </div>
  </div>

  <!-- Filter Bar -->
  <div class="rounded-xl border bg-white px-4 py-1">
    <app-global-filter-panel [config]="filterConfig" (filtersChanged)="onFiltersChanged($event)" />
  </div>

  <!-- KPI Row -->
  <app-kpi-card-grid [cards]="ticketKpis" title="TICKET OVERVIEW" [columns]="5" />

  <!-- Middle Row: Open Requests by Type & Community + Open Tickets -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <!-- Requests by Type -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-50">
            <mat-icon class="!text-sm text-purple-500">category</mat-icon>
          </div>
          <h3 class="text-default text-sm font-bold">Open Requests by Type &amp; Community</h3>
        </div>
      </div>
      @if (!hasTicketPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingCategory) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (categoryData?.by_type?.length > 0) {
        <!-- By Type -->
        <div class="mb-2 flex items-center gap-2">
          <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Type</span>
          <div class="flex-1 border-t border-gray-100"></div>
        </div>
        <div class="mb-3 flex flex-col gap-1.5">
          @for (t of categoryData.by_type.slice(0, 5); track $index) {
            <a
              routerLink="/happiness-center/online"
              [queryParams]="{ type: t.type }"
              class="flex items-center gap-2 rounded-lg border border-gray-100 bg-white px-2.5 py-2 shadow-sm transition-colors hover:bg-gray-50"
            >
              <div class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-purple-50">
                <mat-icon class="!text-xs !leading-none text-purple-500">label</mat-icon>
              </div>
              <span class="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide text-gray-700">{{ t.type || 'Other' }}</span>
              <span class="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold" [ngClass]="getTypeBadgeClass(t.type)">{{ t.count }}</span>
            </a>
          }
        </div>
        <!-- By Community -->
        @if (categoryData.by_community?.length > 0) {
          <div class="mb-2 flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-gray-400">By Community</span>
            <div class="flex-1 border-t border-gray-100"></div>
          </div>
          <div class="flex flex-col gap-1">
            @for (c of categoryData.by_community.slice(0, 4); track $index) {
              <div class="flex items-center gap-2.5 rounded-lg border border-gray-100 bg-white px-2.5 py-2 shadow-sm transition-colors hover:bg-gray-50">
                <div class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-50">
                  <mat-icon class="!text-sm !leading-none text-blue-500">apartment</mat-icon>
                </div>
                <span class="min-w-0 flex-1 truncate text-xs font-semibold text-gray-800">{{ c.community_name || 'N/A' }}</span>
                <div class="flex shrink-0 items-center gap-1.5">
                  <span class="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600">{{ c.count }}</span>
                </div>
              </div>
            }
          </div>
        }
      } @else if (!loadingCategory) {
        <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
          <mat-icon class="!text-3xl text-gray-300">category</mat-icon>
          <span class="text-xs text-gray-400">No request data available</span>
        </div>
      }
    </div>

    <!-- Open Tickets -->
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div class="mb-3 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <div class="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50">
            <mat-icon class="!text-sm text-indigo-500">support_agent</mat-icon>
          </div>
          <div>
            <h3 class="text-default text-sm font-bold">Open Tickets</h3>
            <p class="text-secondary text-xs">Oldest unresolved requests, sorted by age</p>
          </div>
        </div>
        @if (openData?.tickets?.length > 0) {
          <span class="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-bold text-indigo-700">{{ openData.total ?? openData.tickets.length }}</span>
        }
      </div>
      @if (!hasTicketPermission) {
        <div class="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700">
          <mat-icon class="!text-base text-red-400">lock</mat-icon>
          Permissions Restricted
        </div>
      } @else if (loadingOpen) {
        <div class="flex justify-center py-6"><mat-spinner diameter="28" /></div>
      } @else if (openData?.tickets?.length > 0) {
        <div class="flex flex-col gap-1">
          @for (t of openData.tickets.slice(0, 5); track $index) {
            <div
              class="flex items-center gap-2.5 overflow-hidden rounded-lg border border-l-4 border-gray-100 bg-white py-2 pl-2.5 pr-2.5 shadow-sm transition-colors hover:bg-gray-50"
              [class.border-l-red-500]="t.age_days > 7"
              [class.border-l-orange-400]="t.age_days > 3 && t.age_days <= 7"
              [class.border-l-blue-300]="t.age_days <= 3"
            >
              <!-- Icon -->
              <div
                class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md"
                [class.bg-red-50]="t.age_days > 7"
                [class.bg-orange-50]="t.age_days > 3 && t.age_days <= 7"
                [class.bg-blue-50]="t.age_days <= 3"
              >
                <mat-icon class="!text-sm !leading-none" [class.text-red-500]="t.age_days > 7" [class.text-orange-500]="t.age_days > 3 && t.age_days <= 7" [class.text-blue-400]="t.age_days <= 3"
                  >confirmation_number</mat-icon
                >
              </div>
              <!-- Content -->
              <div class="min-w-0 flex-1">
                <div class="flex items-baseline gap-1.5">
                  <p class="line-clamp-1 text-xs font-semibold uppercase leading-snug text-gray-800">{{ t.type || '—' }}</p>
                  @if (t.reference) {
                    <span class="flex-shrink-0 font-mono text-[10px] text-gray-400">{{ t.reference }}</span>
                  }
                </div>
                <div class="mt-0.5 flex flex-wrap items-center gap-x-1 text-[10px] text-gray-400">
                  @if (t.community_name) {
                    <span>{{ t.community_name }}</span>
                    <span class="text-gray-300">·</span>
                  }
                  <span>{{ t.assigned_to || 'Unassigned' }}</span>
                </div>
              </div>
              <!-- Status + age -->
              <div class="flex flex-shrink-0 flex-col items-end gap-1">
                <span class="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" [ngClass]="getStatusBadgeClass(t.status)">{{ t.status }}</span>
                <span
                  class="rounded-full px-1.5 py-0.5 text-[9px] font-bold"
                  [class.bg-red-100]="t.age_days > 7"
                  [class.text-red-700]="t.age_days > 7"
                  [class.bg-orange-100]="t.age_days > 3 && t.age_days <= 7"
                  [class.text-orange-700]="t.age_days > 3 && t.age_days <= 7"
                  [class.bg-gray-100]="t.age_days <= 3"
                  [class.text-gray-500]="t.age_days <= 3"
                  >{{ t.age_days }}d</span
                >
              </div>
            </div>
          }
        </div>
      } @else if (!loadingOpen) {
        <div class="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-gray-200 py-8 text-center">
          <mat-icon class="!text-3xl text-gray-300">inbox</mat-icon>
          <span class="text-xs text-gray-400">No open tickets</span>
        </div>
      }
    </div>
  </div>

  <!-- Bottom Row: Surveys + Timeline -->
  <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-survey-overview [dashboardType]="dashboardType" [filters]="filters" />
    </div>
    <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <app-activity-timeline [requiredPermission]="'activity_logs_other'" />
    </div>
  </div>
</div>
